import { BlogPost } from './blogPostsData';

const WORDPRESS_API_BASE =
  process.env.REACT_APP_WORDPRESS_API_BASE ||
  'https://infoea3c9a03652-qoect.wpcomstaging.com/wp-json/wp/v2';

interface WordPressRenderedValue {
  rendered?: string;
}

interface WordPressMedia {
  source_url?: string;
}

interface WordPressImageField {
  ID?: number;
  id?: number;
  url?: string;
  source_url?: string;
  sizes?: {
    full?: string;
    large?: string;
    [key: string]: string | undefined;
  };
}

interface WordPressPost {
  id: number;
  slug: string;
  date: string;
  title: WordPressRenderedValue;
  excerpt?: WordPressRenderedValue;
  featured_media?: number;
  acf?: {
    description?: string;
    thumbnail_image?: string | number | WordPressImageField;
    hero_image?: string | number | WordPressImageField;
    introduction?: string;
    main_content?: string;
    author_signature?: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: WordPressMedia[];
  };
}

const mediaUrlCache = new Map<number, Promise<string | undefined>>();

const stripHtml = (value: string): string =>
  value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

const decodeHtmlEntities = (value: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const normalizeWordPressHtml = (value: string): string => {
  const decodedValue = decodeHtmlEntities(value).trim();

  return decodedValue
    .replace(/<div>\s*/gi, '')
    .replace(/\s*<\/div>/gi, '')
    .replace(/^\s*['"]|['"],?\s*$/gm, '')
    .trim();
};

const formatPostDate = (date: string): string => {
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const fetchMediaUrl = async (mediaId: number): Promise<string | undefined> => {
  if (!mediaUrlCache.has(mediaId)) {
    mediaUrlCache.set(
      mediaId,
      fetch(`${WORDPRESS_API_BASE}/media/${mediaId}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch WordPress media ${mediaId}`);
          }
          return response.json() as Promise<WordPressMedia>;
        })
        .then((media) => media.source_url)
        .catch(() => undefined)
    );
  }

  return mediaUrlCache.get(mediaId);
};

const resolveImageUrl = async (
  image: string | number | WordPressImageField | undefined,
  fallback?: string
): Promise<string | undefined> => {
  if (typeof image === 'object' && image !== null) {
    if (image.source_url) {
      return image.source_url;
    }

    if (image.url) {
      return image.url;
    }

    if (image.sizes?.full) {
      return image.sizes.full;
    }

    if (image.sizes?.large) {
      return image.sizes.large;
    }

    const imageId = image.ID || image.id;
    if (imageId) {
      return (await fetchMediaUrl(imageId)) || fallback;
    }
  }

  if (typeof image === 'string' && image.trim()) {
    return image;
  }

  if (typeof image === 'number' && image > 0) {
    return (await fetchMediaUrl(image)) || fallback;
  }

  return fallback;
};

const mapWordPressPost = async (post: WordPressPost): Promise<BlogPost> => {
  const acf = post.acf || {};
  const embeddedImage = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
  const title = decodeHtmlEntities(post.title.rendered || 'Untitled');
  const description =
    acf.description ? normalizeWordPressHtml(acf.description) :
    stripHtml(post.excerpt?.rendered || '') ||
    stripHtml(normalizeWordPressHtml(acf.introduction || ''));

  const [thumbnailImage, heroImage] = await Promise.all([
    resolveImageUrl(acf.thumbnail_image, embeddedImage),
    resolveImageUrl(acf.hero_image),
  ]);

  return {
    id: `wp-${post.id}`,
    slug: post.slug,
    image: thumbnailImage || '/images/blogThumbnail.webp',
    flagImage: heroImage || '/images/Blog3Hero.webp',
    title,
    description,
    author: 'Rob Bruggeman',
    authorImage: '/images/RobertBruggeman.webp',
    date: formatPostDate(post.date),
    link: `/blog/${post.slug}`,
    introduction: acf.introduction ? [normalizeWordPressHtml(acf.introduction)] : undefined,
    contentSections: acf.main_content
      ? [
          {
            title: '',
            content: [],
            contentHtml: normalizeWordPressHtml(acf.main_content),
          },
        ]
      : undefined,
    authorSignature:
      acf.author_signature?.replace(/^["']|["']$/g, '').replace(/\\n/g, '\n') ||
      'Good investing,\nRob Bruggeman, CFA',
  };
};

const fetchWordPressJson = async <T>(path: string): Promise<T> => {
  const response = await fetch(`${WORDPRESS_API_BASE}${path}`);
  if (!response.ok) {
    throw new Error(`WordPress API request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
};

export const fetchWordPressPosts = async (): Promise<BlogPost[]> => {
  const posts = await fetchWordPressJson<WordPressPost[]>('/posts?per_page=100&_embed=1');
  return Promise.all(posts.map(mapWordPressPost));
};

export const fetchWordPressPostBySlug = async (
  slug: string
): Promise<BlogPost | undefined> => {
  const posts = await fetchWordPressJson<WordPressPost[]>(
    `/posts?slug=${encodeURIComponent(slug)}&_embed=1`
  );

  if (!posts[0]) {
    return undefined;
  }

  return mapWordPressPost(posts[0]);
};

export const mergeBlogPosts = (
  fallbackPosts: BlogPost[],
  wordpressPosts: BlogPost[]
): BlogPost[] => {
  const postsBySlug = new Map<string, BlogPost>();

  fallbackPosts.forEach((post) => postsBySlug.set(post.slug, post));
  wordpressPosts.forEach((post) => postsBySlug.set(post.slug, post));

  return Array.from(postsBySlug.values());
};
